import { useState, useCallback } from 'react';
import { toast } from 'sonner';

import { JuaForecastRequest } from '@app/api/forecasting/types';
import { useError } from '@app/hooks/useError';
import Logger from '@app/utils/logger';
import { ForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';

import { getJuaForecast } from '../utils/forecastApiUtils';
import { PlottableForecast } from '../types';

export function useForecastApi() {
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  const fetchForecast = useCallback(
    async (
      modelId: ForecastModel,
      request: JuaForecastRequest,
    ): Promise<PlottableForecast | null> => {
      setLoading(true);
      try {
        Logger.info('Fetching forecast data');
        return await getJuaForecast(modelId, request);
      } catch (error: any) {
        Logger.error('Failed to fetch forecast', { error, modelId });

        if (error?.status === 400 && error?.details?.type === 'X-No-Data-At-Location') {
          if (
            error?.details?.model_name &&
            error?.details?.requested_lat &&
            error?.details?.requested_lon
          ) {
            const modelObj = FORECAST_MODELS.find((model) => model.id === modelId);
            const modelDisplayName = modelObj?.label || modelId;

            toast.error(`No data is available for ${modelDisplayName} at this location`, {
              duration: 4000,
              id: `no-data-${modelId}-${error.details.requested_lat}-${error.details.requested_lon}`,
            });

            return null;
          }
        }

        showError({
          code: 'FORECAST_API_ERROR',
          message: `Failed to fetch forecast from ${modelId}`,
          severity: 'error',
          details: { model: modelId, error },
          suggestion: 'Please try again later or contact support if the issue persists.',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [showError],
  );

  return {
    loading,
    fetchForecast,
  };
}
